/* AYU Video Page – Fokus: Video anschauen → Termin buchen
 * Nur Video + klarer CTA zur /termin Seite
 * Kein YouTube-Weiterleitung (rel=0, modestbranding)
 */
import { useEffect } from "react";
import { IMAGES } from "@/lib/constants";
import { useI18n } from "@/contexts/I18nContext";
import {
  ArrowRight, CalendarDays, Shield, Clock, CheckCircle, Play, Sparkles
} from "lucide-react";

export default function VideoPage() {
  const { t } = useI18n();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Meta Pixel: ViewContent-Event bei Video-Seite
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'ViewContent', {
        content_name: 'AYU Video',
        content_category: 'Funnel',
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-[oklch(0.14_0.02_280)]">
      {/* ── Minimal Header ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[oklch(0.14_0.02_280/0.95)] backdrop-blur-md border-b border-[oklch(0.25_0.02_280)]">
        <div className="container flex items-center justify-between py-4">
          <a href="/" className="flex items-center gap-3">
            <img src={IMAGES.logo} alt="AYU Logo" className="h-10 w-auto" />
          </a>
          <a
            href="/termin"
            className="btn-olive px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide flex items-center gap-2"
          >
            <CalendarDays className="w-4 h-4" />
            {t("video.navCta")}
          </a>
        </div>
      </header>

      {/* ── 1. Hero with Video ── */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 mandala-overlay" />
        <div className="container relative z-10 text-center">
          <span className="inline-block text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)] mb-4">
            {t("video.heroBadge")}
          </span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-[oklch(0.93_0.015_80)] mb-4 leading-tight"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("video.heroTitle1")}<br />
            <span className="italic text-[oklch(0.52_0.09_128)]">{t("video.heroTitle2")}</span>
          </h1>
          <p className="text-lg text-[oklch(0.7_0.02_80)] max-w-2xl mx-auto mb-10" style={{ fontFamily: "var(--font-body)" }}>
            {t("video.heroDesc")}
          </p>

          {/* YouTube Video Embed */}
          <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl border border-[oklch(0.78_0.11_85/0.2)]">
            <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
              <iframe
                src="https://www.youtube-nocookie.com/embed/IAs5BFEVzFA?rel=0&modestbranding=1"
                title={t("video.iframeTitle")}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center justify-center gap-6 mt-6 text-sm text-[oklch(0.6_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4 text-[oklch(0.52_0.09_128)]" />
              {t("video.badgeFree")}
            </span>
            <span>·</span>
            <span>{t("video.badgeNoDiagnosis")}</span>
            <span>·</span>
            <span>{t("video.badgeNoObligation")}</span>
          </div>
        </div>
      </section>

      {/* ── 2. Demo ausprobieren ── */}
      <section className="py-16 bg-[oklch(0.18_0.02_280)]">
        <div className="container max-w-3xl text-center">
          <span className="inline-block text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.78_0.11_85)] mb-3">
            {t("video.demoBadge")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[oklch(0.93_0.015_80)] mb-4"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("video.demoTitle1")}{" "}
            <span className="italic text-[oklch(0.78_0.11_85)]">{t("video.demoTitle2")}</span>
          </h2>
          <p className="text-lg text-[oklch(0.7_0.02_80)] leading-relaxed mb-8 max-w-xl mx-auto" style={{ fontFamily: "var(--font-body)" }}>
            {t("video.demoDesc")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-[oklch(0.78_0.11_85)]" />
              <span className="text-sm text-[oklch(0.6_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{t("video.demoLiveDuration")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-[oklch(0.78_0.11_85)]" />
              <span className="text-sm text-[oklch(0.6_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{t("video.demoNoRegistration")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[oklch(0.78_0.11_85)]" />
              <span className="text-sm text-[oklch(0.6_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>{t("video.demoAllFeatures")}</span>
            </div>
          </div>

          <a
            href="/demo"
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-[oklch(0.15_0.02_85)] text-lg font-bold transition-all duration-300 hover:scale-105 hover:shadow-xl"
            style={{ background: "oklch(0.78 0.11 85)", fontFamily: "var(--font-body)" }}
          >
            <Play className="w-5 h-5" />
            {t("video.demoButton")}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* ── 3. CTA zur Termin-Seite ── */}
      <section className="py-20 bg-[oklch(0.97_0.01_80)]">
        <div className="container max-w-3xl text-center">
          <span className="inline-block text-sm font-semibold tracking-[0.2em] uppercase text-[oklch(0.52_0.09_128)] mb-3">
            {t("video.ctaBadge")}
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[oklch(0.25_0.03_50)] mb-6"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {t("video.ctaTitle")}
          </h2>
          <p className="text-lg text-[oklch(0.45_0.04_50)] leading-relaxed mb-10" style={{ fontFamily: "var(--font-body)" }}>
            {t("video.ctaDesc")}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-10">
            {[
              { icon: Shield, text: t("video.ctaBadge1") },
              { icon: Clock, text: t("video.ctaBadge2") },
              { icon: CheckCircle, text: t("video.ctaBadge3") },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <item.icon className="w-5 h-5 text-[oklch(0.52_0.09_128)]" />
                <span className="text-sm text-[oklch(0.4_0.02_80)] font-medium" style={{ fontFamily: "var(--font-body)" }}>
                  {item.text}
                </span>
              </div>
            ))}
          </div>

          <a
            href="/termin"
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-white text-lg font-bold transition-all duration-300 hover:scale-105 hover:shadow-xl"
            style={{ background: "oklch(0.52 0.09 128)", fontFamily: "var(--font-body)" }}
          >
            <CalendarDays className="w-5 h-5" />
            {t("video.ctaButton")}
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* ── Annam Quote ── */}
      <section className="py-16 bg-[oklch(0.14_0.02_280)]">
        <div className="container max-w-3xl text-center">
          <div className="w-20 h-20 rounded-full mx-auto mb-6 overflow-hidden border-2 border-[oklch(0.78_0.11_85/0.5)]">
            <img
              src={IMAGES.annamOrange}
              alt="Annam Hanfstein"
              className="w-full h-full object-cover"
            />
          </div>
          <blockquote
            className="text-xl md:text-2xl italic text-[oklch(0.85_0.01_80)] leading-relaxed mb-4"
            style={{ fontFamily: "var(--font-display)" }}
          >
            "{t("video.quoteText")}"
          </blockquote>
          <p className="font-bold text-[oklch(0.93_0.015_80)]" style={{ fontFamily: "var(--font-display)" }}>
            {t("video.quoteAuthor")}
          </p>
          <p className="text-sm text-[oklch(0.6_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>
            {t("video.quoteRole")}
          </p>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-[oklch(0.12_0.02_280)] py-10 border-t border-[oklch(0.2_0.02_280)]">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={IMAGES.logo} alt="AYU Logo" className="h-8 w-auto" />
            <div className="flex items-center gap-6 text-sm text-[oklch(0.5_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>
              <a href="/impressum" className="hover:text-[oklch(0.78_0.11_85)] transition-colors">{t("video.footerImpressum")}</a>
              <a href="/datenschutz" className="hover:text-[oklch(0.78_0.11_85)] transition-colors">{t("video.footerDatenschutz")}</a>
              <a href="mailto:office@ayu-rveda.ai" className="hover:text-[oklch(0.78_0.11_85)] transition-colors">{t("video.footerKontakt")}</a>
            </div>
          </div>
          <div className="gold-line my-6" />
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-xs text-[oklch(0.4_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>
              &copy; {new Date().getFullYear()} AYU Healthtech LIMITED · 128 City Road, London, EC1V 2NX
            </p>
            <p className="text-xs text-[oklch(0.35_0.02_80)]" style={{ fontFamily: "var(--font-body)" }}>
              {t("video.footerDisclaimer")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
